import { InboxOutlined } from "@ant-design/icons"
import { Alert, DatePicker, Form, List, Modal, Select, Typography, Upload } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../components/Input/TextArea.tsx"
import { fileExternalSignature } from "../api/external-signatures.services.ts"
import type {
  ExternalSignature,
  LiveSignatureRequest
} from "../types/external-signature.types.ts"

const { Text } = Typography

const ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
// Only an approved or sent contract can be filed as signed; the server refuses the rest.
const SIGNABLE_CONTRACT_STATUSES = new Set(["approved", "sent"])

export interface ContractOption {
  id: string
  label: string
  status: string
}

interface UploadSignedDocumentModalProps {
  leadId: string
  contracts: ContractOption[]
  onCancel: () => void
  onFiled: (signature: ExternalSignature) => void
}

interface FormValues {
  signedOn: Dayjs
  signers: string[]
  contract?: string
  note?: string
}

export function UploadSignedDocumentModal({
  leadId,
  contracts,
  onCancel,
  onFiled
}: UploadSignedDocumentModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const [file, setFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [liveRequests, setLiveRequests] = useState<LiveSignatureRequest[] | null>(null)
  const choosable = contracts.filter((contract) => SIGNABLE_CONTRACT_STATUSES.has(contract.status))

  const submit = async () => {
    let values: FormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    if (!file) {
      setRefusal(t("esign.external.fileRequired"))
      return
    }
    setSaving(true)
    setRefusal(null)
    const outcome = await fileExternalSignature({
      lead: leadId,
      file,
      signedOn: values.signedOn.format("YYYY-MM-DD"),
      signers: values.signers.map((name) => name.trim()).filter(Boolean),
      note: values.note?.trim(),
      contract: values.contract ?? null,
      withdrawLiveRequests: liveRequests !== null
    })
    setSaving(false)
    if (outcome.kind === "filed") {
      onFiled(outcome.signature)
      return
    }
    if (outcome.kind === "live_request") {
      setLiveRequests(outcome.requests)
      return
    }
    const firstFieldError = Object.values(outcome.errors ?? {})[0]?.[0]
    setRefusal(outcome.message ?? firstFieldError ?? t("esign.external.failed"))
  }

  return (
    <Modal
      open
      title={t("esign.external.title")}
      okText={liveRequests ? t("esign.external.withdrawAndFile") : t("esign.external.file")}
      okButtonProps={{ danger: liveRequests !== null }}
      cancelText={t("esign.common.cancel")}
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      maskClosable={false}
      onOk={submit}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        className="mb-4"
        message={t("esign.external.provenanceTitle")}
        description={t("esign.external.provenanceBody")}
      />
      {liveRequests && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          data-testid="external-live-requests"
          message={t("esign.external.liveTitle")}
          description={
            <>
              <div className="mb-2">{t("esign.external.liveBody", { count: liveRequests.length })}</div>
              <List
                size="small"
                dataSource={liveRequests}
                rowKey={(request) => request.id}
                renderItem={(request) => (
                  <List.Item className="!px-0">
                    <Text strong>{request.document_name}</Text>
                    <Text type="secondary" className="text-xs">
                      {t(`esign.recipientStatuses.${request.furthest_progress}`)}
                    </Text>
                  </List.Item>
                )}
              />
            </>
          }
        />
      )}
      {refusal && <Alert type="error" showIcon className="mb-4" message={refusal} />}
      <Upload.Dragger
        accept={ACCEPT}
        multiple={false}
        showUploadList={false}
        disabled={saving}
        beforeUpload={(candidate) => {
          setFile(candidate)
          setRefusal(null)
          return false
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <Text>{t("esign.external.dragHint")}</Text>
        <br />
        <Text type="secondary">{t("esign.external.limits")}</Text>
      </Upload.Dragger>
      {file && (
        <Text className="mt-2 block" data-testid="external-chosen-file">
          {t("esign.choose.selected", { name: file.name })}
        </Text>
      )}
      <Form form={form} layout="vertical" disabled={saving} className="mt-4">
        <Form.Item
          name="signedOn"
          label={t("esign.external.signedOn")}
          extra={t("esign.external.signedOnHelp")}
          rules={[{ required: true, message: t("esign.external.signedOnRequired") }]}
        >
          <DatePicker
            className="w-full"
            format="MMM D, YYYY"
            disabledDate={(day) => day.isAfter(dayjs(), "day")}
          />
        </Form.Item>
        <Form.Item
          name="signers"
          label={t("esign.external.signers")}
          rules={[
            {
              required: true,
              type: "array",
              min: 1,
              message: t("esign.external.signersRequired")
            }
          ]}
        >
          <Select mode="tags" open={false} tokenSeparators={[","]} placeholder={t("esign.external.signersPlaceholder")} />
        </Form.Item>
        {choosable.length > 0 && (
          <Form.Item name="contract" label={t("esign.external.contract")} extra={t("esign.external.contractHelp")}>
            <Select
              allowClear
              options={choosable.map((contract) => ({ value: contract.id, label: contract.label }))}
            />
          </Form.Item>
        )}
        <Form.Item name="note" label={t("esign.external.note")}>
          <TextArea rows={2} maxLength={2000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  )
}
